import { useState, useEffect } from "react";
import Modal from "react-bootstrap/Modal";
import Button from "react-bootstrap/Button";
import Form from "react-bootstrap/Form";
import { FaHeartbeat } from "react-icons/fa";
import { MdPersonPin } from "react-icons/md";
import { GiHandBandage, GiLungs } from "react-icons/gi";
import { PiBedFill } from "react-icons/pi";
import "./style.css";

const ALARM_TYPES = [
	{
		key: "vitals",
		label: "Kalp Atış Hızı",
		icon: FaHeartbeat,
		color: "#ef4444",
	},
	{
		key: "breathRate",
		label: "Solunum Sayısı",
		icon: GiLungs,
		color: "#06b6d4",
	},
	{
		key: "roomEntryExit",
		label: "Odaya Giriş / Çıkış",
		icon: MdPersonPin,
		color: "#3b82f6",
	},
	{
		key: "fall",
		label: "Düşme Algılama",
		icon: GiHandBandage,
		color: "#f59e0b",
	},
	{
		key: "bedEntryExit",
		label: "Yatak Giriş / Çıkış",
		icon: PiBedFill,
		color: "#22c55e",
	},
];

const RISK_CONFIGS = {
	vitals: {
		unit: "bpm",
		minField: "heartRateMin",
		maxField: "heartRateMax",
		rangeMin: 20,
		rangeMax: 200,
		presets: {
			low: { min: 30, max: 140 },
			high: { min: 40, max: 130 },
		},
	},
	breathRate: {
		unit: "/dk",
		minField: "breathRateMin",
		maxField: "breathRateMax",
		rangeMin: 1,
		rangeMax: 60,
		presets: {
			low: { min: 7, max: 30 },
			high: { min: 7, max: 20 },
		},
	},
};

const DEFAULT_SETTINGS = {
	vitals: {
		enabled: true,
		timeRanges: [],
		riskLevel: "custom",
		heartRateMin: 30,
		heartRateMax: 140,
	},
	breathRate: {
		enabled: false,
		timeRanges: [],
		riskLevel: "custom",
		breathRateMin: 7,
		breathRateMax: 30,
	},
	roomEntryExit: { enabled: true, timeRanges: [] },
	fall: { enabled: true, timeRanges: [] },
	bedEntryExit: { enabled: true, timeRanges: [] },
};

const RiskLevelSection = ({ setting, onChange, config }) => {
	const riskLevel = setting.riskLevel || "custom";
	const minVal = setting[config.minField] ?? config.presets.low.min;
	const maxVal = setting[config.maxField] ?? config.presets.low.max;
	const rangeSpan = config.rangeMax - config.rangeMin;

	const handleRiskChange = (level) => {
		if (level === "custom") {
			onChange({ ...setting, riskLevel: "custom" });
		} else {
			onChange({
				...setting,
				riskLevel: level,
				[config.minField]: config.presets[level].min,
				[config.maxField]: config.presets[level].max,
			});
		}
	};

	return (
		<div className="vitals-risk-section">
			<span className="time-ranges-label">RİSK SEVİYESİ</span>

			<div className="risk-options">
				<label className={`risk-option ${riskLevel === "low" ? "selected" : ""}`}>
					<input
						type="radio"
						name={`riskLevel-${config.minField}`}
						checked={riskLevel === "low"}
						onChange={() => handleRiskChange("low")}
					/>
					<div className="risk-option-content">
						<span className="risk-option-label">Düşük Riskli Kullanıcı</span>
						<span className="risk-option-range">
							{config.presets.low.min} {config.unit} — {config.presets.low.max} {config.unit}
						</span>
					</div>
				</label>

				<label className={`risk-option ${riskLevel === "high" ? "selected" : ""}`}>
					<input
						type="radio"
						name={`riskLevel-${config.minField}`}
						checked={riskLevel === "high"}
						onChange={() => handleRiskChange("high")}
					/>
					<div className="risk-option-content">
						<span className="risk-option-label">Yüksek Riskli Kullanıcı</span>
						<span className="risk-option-range">
							{config.presets.high.min} {config.unit} — {config.presets.high.max} {config.unit}
						</span>
					</div>
				</label>

				<label className={`risk-option ${riskLevel === "custom" ? "selected" : ""}`}>
					<input
						type="radio"
						name={`riskLevel-${config.minField}`}
						checked={riskLevel === "custom"}
						onChange={() => handleRiskChange("custom")}
					/>
					<div className="risk-option-content">
						<span className="risk-option-label">Özel Seçim</span>
						{riskLevel === "custom" && (
							<span className="risk-option-range">
								{minVal} {config.unit} — {maxVal} {config.unit}
							</span>
						)}
					</div>
				</label>
			</div>

			{riskLevel === "custom" && (
				<div className="custom-range-section">
					<div className="custom-range-row">
						<span className="custom-range-label">ARALIK BELİRLE</span>
						<div className="custom-range-values">
							<Form.Control
								type="number"
								min={config.rangeMin}
								max={maxVal - 1}
								value={minVal}
								onChange={(e) =>
									onChange({
										...setting,
										[config.minField]: Number(e.target.value),
									})
								}
								className="custom-range-input"
							/>
							<span className="time-range-separator">—</span>
							<Form.Control
								type="number"
								min={minVal + 1}
								max={config.rangeMax}
								value={maxVal}
								onChange={(e) =>
									onChange({
										...setting,
										[config.maxField]: Number(e.target.value),
									})
								}
								className="custom-range-input"
							/>
							<span className="custom-range-unit">{config.unit}</span>
						</div>
					</div>
					<div className="custom-range-slider">
						<input
							type="range"
							min={config.rangeMin}
							max={config.rangeMax}
							value={minVal}
							onChange={(e) => {
								const val = Number(e.target.value);
								if (val < maxVal) {
									onChange({ ...setting, [config.minField]: val });
								}
							}}
							className="range-slider range-slider-min"
						/>
						<input
							type="range"
							min={config.rangeMin}
							max={config.rangeMax}
							value={maxVal}
							onChange={(e) => {
								const val = Number(e.target.value);
								if (val > minVal) {
									onChange({ ...setting, [config.maxField]: val });
								}
							}}
							className="range-slider range-slider-max"
						/>
						<div
							className="range-slider-track"
							style={{
								left: `${((minVal - config.rangeMin) / rangeSpan) * 100}%`,
								width: `${((maxVal - minVal) / rangeSpan) * 100}%`,
							}}
						/>
					</div>
					<div className="range-labels">
						<span>{config.rangeMin}</span>
						<span>{config.rangeMax}</span>
					</div>
				</div>
			)}

			<div className="risk-info-box">
				<strong>Yüksek Riskli Kullanıcı Kimdir?</strong>
				<p>
					Kalp krizi geçirmiş, kalp yetersizliği olan, amfizemi olan,
					felç geçirmiş veya yeni ameliyat geçirmiş kişiler yüksek
					riskli kullanıcı olarak değerlendirilir.
				</p>
			</div>
		</div>
	);
};

const AlarmTypeDetail = ({ typeKey, typeLabel, setting, onChange }) => {
	const [newStart, setNewStart] = useState("");
	const [newEnd, setNewEnd] = useState("");

	const addTimeRange = () => {
		if (newStart && newEnd) {
			onChange({
				...setting,
				timeRanges: [
					...(setting.timeRanges || []),
					{ start: newStart, end: newEnd },
				],
			});
			setNewStart("");
			setNewEnd("");
		}
	};

	const removeTimeRange = (index) => {
		onChange({
			...setting,
			timeRanges: setting.timeRanges.filter((_, i) => i !== index),
		});
	};

	const riskConfig = RISK_CONFIGS[typeKey];

	return (
		<div className="alarm-type-detail">
			<div className="alarm-type-header">
				<h6 className="alarm-type-title">{typeLabel}</h6>
				<Form.Check
					type="switch"
					id={`alarm-toggle-${typeKey}`}
					checked={setting.enabled}
					onChange={(e) =>
						onChange({ ...setting, enabled: e.target.checked })
					}
					label={setting.enabled ? "Açık" : "Kapalı"}
					className="alarm-toggle-switch"
				/>
			</div>

			{setting.enabled && (
			<>
				<div className="alarm-time-ranges">
					<span className="time-ranges-label">
						AKTİF ZAMAN ARALIKLARI
					</span>

					{(!setting.timeRanges || setting.timeRanges.length === 0) && (
						<small className="text-muted">
							Zaman aralığı tanımlanmadı — alarm 7/24 aktif.
						</small>
					)}

					{setting.timeRanges?.map((range, index) => (
						<div key={index} className="time-range-row">
							<div className="time-range-values">
								<span className="time-range-value">
									{range.start}
								</span>
								<span className="time-range-separator">—</span>
								<span className="time-range-value">
									{range.end}
								</span>
							</div>
							<span
								className="time-range-remove"
								onClick={() => removeTimeRange(index)}
							>
								&times;
							</span>
						</div>
					))}

					<div className="time-range-add-section">
						<div className="time-range-inputs">
							<Form.Control
								type="time"
								value={newStart}
								onChange={(e) => setNewStart(e.target.value)}
								className="time-input"
							/>
							<span className="time-range-separator">—</span>
							<Form.Control
								type="time"
								value={newEnd}
								onChange={(e) => setNewEnd(e.target.value)}
								className="time-input"
							/>
						</div>
						<Button
							variant="outline-secondary"
							size="sm"
							onClick={addTimeRange}
							disabled={!newStart || !newEnd}
							className="time-range-add-btn"
						>
							+ Zaman Aralığı Ekle
						</Button>
					</div>
				</div>

				{riskConfig && (
					<RiskLevelSection
						setting={setting}
						onChange={onChange}
						config={riskConfig}
					/>
				)}
			</>
			)}
		</div>
	);
};

const HobacareAlarmSettings = ({ deviceId, show, onHide }) => {
	const [settings, setSettings] = useState(DEFAULT_SETTINGS);
	const [selectedType, setSelectedType] = useState(null);
	const [saving, setSaving] = useState(false);
	const [loading, setLoading] = useState(false);

	useEffect(() => {
		if (show && deviceId) {
			fetchSettings();
			setSelectedType(null);
		}
	}, [show, deviceId]);

	const fetchSettings = async () => {
		setLoading(true);
		try {
			const response = await fetch(
				`https://app.vivasmartcare.com/api/hobacare-alarm-settings/${deviceId}`
			);
			const data = await response.json();
			if (data.success && data.alarmSettings) {
				const merged = { ...DEFAULT_SETTINGS };
				for (const key of Object.keys(DEFAULT_SETTINGS)) {
					if (data.alarmSettings[key]) {
						merged[key] = {
							...DEFAULT_SETTINGS[key],
							enabled:
								data.alarmSettings[key].enabled !== undefined
									? data.alarmSettings[key].enabled
									: DEFAULT_SETTINGS[key].enabled,
							timeRanges:
								data.alarmSettings[key].timeRanges || [],
						};
						// Risk level fields for vitals and breathRate
						if (key === "vitals") {
							merged[key].riskLevel =
								data.alarmSettings[key].riskLevel ||
								DEFAULT_SETTINGS.vitals.riskLevel;
							merged[key].heartRateMin =
								data.alarmSettings[key].heartRateMin ??
								DEFAULT_SETTINGS.vitals.heartRateMin;
							merged[key].heartRateMax =
								data.alarmSettings[key].heartRateMax ??
								DEFAULT_SETTINGS.vitals.heartRateMax;
						}
						if (key === "breathRate") {
							merged[key].riskLevel =
								data.alarmSettings[key].riskLevel ||
								DEFAULT_SETTINGS.breathRate.riskLevel;
							merged[key].breathRateMin =
								data.alarmSettings[key].breathRateMin ??
								DEFAULT_SETTINGS.breathRate.breathRateMin;
							merged[key].breathRateMax =
								data.alarmSettings[key].breathRateMax ??
								DEFAULT_SETTINGS.breathRate.breathRateMax;
						}
					}
				}
				setSettings(merged);
			}
		} catch (error) {
			console.error("Alarm ayarları yüklenemedi:", error);
		} finally {
			setLoading(false);
		}
	};

	const handleSave = async () => {
		setSaving(true);
		try {
			const response = await fetch(
				"https://app.vivasmartcare.com/api/hobacare-update-alarm-settings",
				{
					method: "POST",
					headers: { "Content-Type": "application/json" },
					body: JSON.stringify({
						deviceId,
						alarmSettings: settings,
					}),
				}
			);
			const data = await response.json();
			if (data.success) {
				onHide();
			} else {
				alert(data.error || "Ayarlar kaydedilemedi.");
			}
		} catch (error) {
			console.error("Alarm ayarları kaydetme hatası:", error);
			alert("Bağlantı hatası. Lütfen tekrar deneyin.");
		} finally {
			setSaving(false);
		}
	};

	return (
		<Modal show={show} onHide={onHide} centered size="md">
			<Modal.Header closeButton>
				<Modal.Title style={{ fontSize: "16px" }}>
					Alarm Ayarları
				</Modal.Title>
			</Modal.Header>
			<Modal.Body>
				{loading ? (
					<div className="text-center py-3">Yükleniyor...</div>
				) : (
					<>
						<p className="alarm-hint-text">
							Her ikona tıklayarak ayarları düzenleyin
						</p>
						{/* Icon Row */}
						<div className="alarm-icons-row">
							{ALARM_TYPES.map((type) => {
								const IconComponent = type.icon;
								const isEnabled =
									settings[type.key]?.enabled;
								const isSelected =
									selectedType === type.key;
								return (
									<div
										key={type.key}
										className={`alarm-icon-btn ${isSelected ? "active" : ""}`}
										onClick={() =>
											setSelectedType(
												type.key === selectedType
													? null
													: type.key
											)
										}
										title={type.label}
									>
										<div
											className={`alarm-icon-circle ${isEnabled ? "" : "disabled"}`}
											style={{
												backgroundColor: isSelected
													? type.color
													: isEnabled
														? `${type.color}20`
														: "#f3f4f6",
											}}
										>
											<IconComponent
												size={20}
												color={
													isSelected
														? "#fff"
														: isEnabled
															? type.color
															: "#ccc"
												}
											/>
										</div>
										<span
											className={`alarm-status-dot ${isEnabled ? "on" : "off"}`}
										/>
									</div>
								);
							})}
						</div>

						{/* Detail Panel */}
						{selectedType && (
							<AlarmTypeDetail
								typeKey={selectedType}
								typeLabel={
									ALARM_TYPES.find(
										(t) => t.key === selectedType
									)?.label
								}
								setting={
									settings[selectedType] ||
									DEFAULT_SETTINGS[selectedType]
								}
								onChange={(newSetting) => {
									setSettings((prev) => ({
										...prev,
										[selectedType]: newSetting,
									}));
								}}
							/>
						)}
					</>
				)}
			</Modal.Body>
			<Modal.Footer>
				<Button variant="outline-secondary" onClick={onHide}>
					İptal
				</Button>
				<Button
					variant="primary"
					onClick={handleSave}
					disabled={saving || loading}
				>
					{saving ? "Kaydediliyor..." : "Kaydet"}
				</Button>
			</Modal.Footer>
		</Modal>
	);
};

export default HobacareAlarmSettings;
